import { useState, useEffect, useRef } from "react";
import { useDebounced } from "@/utils";
import { get, set, del } from "idb-keyval";

export type BackgroundImageState = {
  horizontal: string;
  vertical: string;
} | null;

export function useRecorderSettings() {
  const isSettingsLoadedRef = useRef(false);
  const [speed, setSpeed] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderSpeed");
    return stored ? parseInt(stored, 10) : 15;
  });
  const [textSize, setTextSize] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderTextSize");
    return stored ? parseInt(stored, 10) : 24;
  });
  const [aspectRatio, setAspectRatio] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderAspectRatio");
    return stored ? parseFloat(stored) : 9 / 16;
  });
  const [bgType, setBgType] = useState<"none" | "blur" | "gradient" | "image">(() => {
    const stored = localStorage.getItem("scRecorderBgType");
    return (stored as any) || "none";
  });
  const [bgBlur, setBgBlur] = useState<number>(() => {
    const stored = localStorage.getItem("scRecorderBgBlur");
    return stored ? parseInt(stored, 10) : 8;
  });
  const [bgColors, setBgColors] = useState<string[]>(() => {
    const stored = localStorage.getItem("scRecorderBgColors");
    try {
      return stored ? JSON.parse(stored) : ["#3E4044", "#1A1B1C"];
    } catch {
      return ["#3E4044", "#1A1B1C"];
    }
  });
  const [bgImage, setBgImage] = useState<BackgroundImageState>(null);
  const [customBgImage, setCustomBgImage] = useState<BackgroundImageState>(null);
  const [bgLogo, setBgLogo] = useState<string | null>(() => {
    return localStorage.getItem("scRecorderBgLogo");
  });
  const [bgLogoStyle, setBgLogoStyle] = useState<"none" | "watermark" | "backdrop" | "repeat">(() => {
    return (localStorage.getItem("scRecorderBgLogoStyle") as any) || "none";
  });

  const [hasPersistedSettings, setHasPersistedSettings] = useState(() => {
    const keys = [
      "scRecorderSpeed",
      "scRecorderTextSize",
      "scRecorderAspectRatio",
      "scRecorderBgType",
      "scRecorderBgBlur",
      "scRecorderBgColors",
      "scRecorderBgImage",
      "scRecorderBgLogo",
      "scRecorderBgLogoStyle",
      "scRecorderPreferredCamera",
      "scRecorderPreferredMic",
    ];
    return keys.some(key => localStorage.getItem(key) !== null);
  });

  // Async-load custom background image on mount from IndexedDB (with localStorage migration path)
  useEffect(() => {
    const loadBgImage = async () => {
      try {
        // 1. Try to load the saved custom background image from IndexedDB
        const savedCustomImage = await get("scRecorderCustomBgImage");
        let activeCustom = savedCustomImage;

        // Backward compatibility migration check
        if (!savedCustomImage) {
          const legacyCustomImage = await get("scRecorderBgImage");
          if (legacyCustomImage) {
            if (typeof legacyCustomImage === "string") {
              activeCustom = { horizontal: legacyCustomImage, vertical: legacyCustomImage };
            } else if (legacyCustomImage.horizontal && legacyCustomImage.vertical) {
              activeCustom = legacyCustomImage;
            }
            await set("scRecorderCustomBgImage", activeCustom);
            await del("scRecorderBgImage");
          }
        }

        if (activeCustom) {
          setCustomBgImage(activeCustom);
        }

        // 2. Load active choice from localStorage
        const activeChoice = localStorage.getItem("scRecorderBgImage");
        if (activeChoice) {
          if (activeChoice === "custom") {
            if (activeCustom) {
              setBgImage(activeCustom);
            }
          } else {
            try {
              const parsed = JSON.parse(activeChoice);
              if (parsed && typeof parsed === "object" && parsed.horizontal) {
                setBgImage(parsed);
              } else {
                setBgImage({ horizontal: activeChoice, vertical: activeChoice });
              }
            } catch {
              setBgImage({ horizontal: activeChoice, vertical: activeChoice });
            }
          }
        }
      } catch (err) {
        console.error("Failed to load background image from IndexedDB:", err);
      } finally {
        isSettingsLoadedRef.current = true;
      }
    };
    loadBgImage();
  }, []);

  const debouncedSpeed = useDebounced(speed, 500);
  const debouncedTextSize = useDebounced(textSize, 500);
  const debouncedBgBlur = useDebounced(bgBlur, 500);
  const debouncedBgColors = useDebounced(bgColors, 500);

  // Persist speed and text size settings
  useEffect(() => {
    localStorage.setItem("scRecorderSpeed", debouncedSpeed.toString());
    setHasPersistedSettings(true);
  }, [debouncedSpeed]);

  useEffect(() => {
    localStorage.setItem("scRecorderTextSize", debouncedTextSize.toString());
    setHasPersistedSettings(true);
  }, [debouncedTextSize]);

  // Persist background settings
  useEffect(() => {
    localStorage.setItem("scRecorderAspectRatio", aspectRatio.toString());
    setHasPersistedSettings(true);
  }, [aspectRatio]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgType", bgType);
    setHasPersistedSettings(true);
  }, [bgType]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgBlur", debouncedBgBlur.toString());
    setHasPersistedSettings(true);
  }, [debouncedBgBlur]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgColors", JSON.stringify(debouncedBgColors));
    setHasPersistedSettings(true);
  }, [debouncedBgColors]);

  // Save custom background image to IndexedDB
  useEffect(() => {
    if (!isSettingsLoadedRef.current) return;
    const saveCustomBg = async () => {
      try {
        if (customBgImage) {
          await set("scRecorderCustomBgImage", customBgImage);
        } else {
          await del("scRecorderCustomBgImage");
        }
      } catch (err) {
        console.error("Failed to save custom background:", err);
      }
    };
    saveCustomBg();
  }, [customBgImage]);

  // Save active background choice
  useEffect(() => {
    if (!isSettingsLoadedRef.current) return;
    const saveBgChoice = async () => {
      try {
        if (bgImage) {
          const isPreset =
            bgImage.horizontal.includes("cdn.socialcoach.io") || bgImage.horizontal.startsWith("data:image/svg+xml");
          if (isPreset) {
            localStorage.setItem("scRecorderBgImage", JSON.stringify(bgImage));
          } else {
            // Active choice is the custom background. We store the "custom" string indicator to localStorage.
            localStorage.setItem("scRecorderBgImage", "custom");
          }
        } else {
          localStorage.removeItem("scRecorderBgImage");
        }
      } catch (err) {
        console.error("Failed to save background choice:", err);
      }
    };
    saveBgChoice();
    setHasPersistedSettings(true);
  }, [bgImage]);

  useEffect(() => {
    if (bgLogo) {
      localStorage.setItem("scRecorderBgLogo", bgLogo);
    } else {
      localStorage.removeItem("scRecorderBgLogo");
    }
    setHasPersistedSettings(true);
  }, [bgLogo]);

  useEffect(() => {
    localStorage.setItem("scRecorderBgLogoStyle", bgLogoStyle);
    setHasPersistedSettings(true);
  }, [bgLogoStyle]);

  return {
    speed,
    setSpeed,
    textSize,
    setTextSize,
    aspectRatio,
    setAspectRatio,
    bgType,
    setBgType,
    bgBlur,
    setBgBlur,
    bgColors,
    setBgColors,
    bgImage,
    setBgImage,
    customBgImage,
    setCustomBgImage,
    bgLogo,
    setBgLogo,
    bgLogoStyle,
    setBgLogoStyle,
    hasPersistedSettings,
    setHasPersistedSettings,
    debouncedBgBlur,
    debouncedBgColors,
  };
}
